"use client";

import type { RegisteredComponent } from "@builder.io/sdk-react";
import dynamic from "next/dynamic";

import { IMAGE_FILE_TYPES } from "@/libs/builder/consts";
import { withValidation } from "@/libs/builder/validators/validateBuilderComponentData";

export const trustedPlacesV2Inputs = [
  {
    name: "logoUrl",
    friendlyName: "RedeemPrizes Dialog Logo",
    type: "file",
    allowedFileTypes: IMAGE_FILE_TYPES,
    required: true,
  },
  {
    name: "logoAlt",
    friendlyName: "Alt Text for Logo",
    type: "string",
    defaultValue: "Logo Alt",
    required: true,
  },
  {
    name: "prizesPageUrl",
    friendlyName: "Prizes Page URL",
    type: "string",
    defaultValue: "#",
  },
  {
    name: "pointsLabel",
    friendlyName: "Points Label",
    type: "string",
    defaultValue: "Points",
    required: true,
  },
  {
    name: "discoverUrlParent",
    friendlyName: "Discover button parent URL",
    type: "string",
    required: false,
  },
  {
    name: "discoverButtonText",
    friendlyName: "Discover button text",
    type: "string",
    required: false,
  },
  {
    name: "pagination",
    type: "number",
    required: true,
    helperText: "How many items to show per load",
    defaultValue: 3,
    min: 1,
  },
  {
    name: "errorMessage",
    type: "string",
    friendlyName: "Error message",
    helperText: "Message displayed when initial prizes load fails",
    required: true,
    defaultValue: "Failed to load prizes",
  },

  {
    name: "loadMoreButtonLabel",
    type: "string",
    friendlyName: "Load more button label",
    required: true,
    defaultValue: "Load more",
  },
  {
    name: "unitLabel",
    type: "string",
    friendlyName: "Unit label",
    defaultValue: "Unit",
    required: true,
  },
  {
    name: "noOffersText",
    type: "string",
    friendlyName: "No offers text",
    required: true,
    defaultValue: "No offers available",
  },
  {
    name: "flotiqContentType",
    type: "string",
    friendlyName: "Flotiq Content Type",
    required: true,
    defaultValue: "TrustedPlacesTest",
  },
  {
    name: "flotiqApiKey",
    type: "string",
    friendlyName: "Flotiq API Key",
    required: true,
    defaultValue: "e3c2e3a790e8f8a058ca470a3540a5c4",
  },
  {
    name: "defaultLat",
    type: "number",
    friendlyName: "Default latitude",
    defaultValue: 50.256132042264184,
    required: true,
    helperText: "The default latitude of the location IF Tenant Country is not defined",
  },
  {
    name: "defaultLng",
    type: "number",
    friendlyName: "Default longitude",
    defaultValue: 19.020268496419725,
    required: true,
    helperText: "The default longitude of the location IF Tenant Country is not defined",
  },
  {
    name: "searchHeader",
    type: "string",
    friendlyName: "Search Header",
    required: true,
    defaultValue: "Search for stores",
  },
  {
    name: "searchBar",
    type: "string",
    friendlyName: "Search Bar placeholder",
    defaultValue: "Search for stores",
    required: true,
  },
  {
    name: "searchButton",
    type: "string",
    friendlyName: "Search button",
    required: true,
    defaultValue: "Search",
  },
  {
    name: "storesCount",
    type: "string",
    friendlyName: "Stores count text",
    required: true,
    defaultValue: "Stores",
  },
  {
    name: "venuePopUpOffersCount",
    type: "string",
    friendlyName: "Rewards count text on venue popup on the map",
    required: true,
    defaultValue: "Rewards",
  },
  {
    name: "rewardsText",
    type: "string",
    friendlyName: "Rewards counter text",
    required: true,
    defaultValue: "Rewards",
  },
  {
    name: "getDirectionLabel",
    type: "string",
    friendlyName: "Get direction label",
    required: true,
    defaultValue: "Get direction",
  },
  {
    name: "backToListLabel",
    type: "string",
    friendlyName: "Back to list label",
    required: true,
    defaultValue: "Back to list",
  },
  {
    name: "currentPositionButtonText",
    type: "string",
    friendlyName: "Current position button",
    required: true,
    defaultValue: "Current position",
  },
  {
    name: "ctaLabel",
    type: "string",
    defaultValue: "Redeem now",
    required: true,
    friendlyName: "Redeem button label on prize card",
  },
  {
    name: "newLabel",
    friendlyName: "Text to show when coupon is new",
    required: true,
    type: "string",
    defaultValue: "New offer",
  },
  {
    name: "newOfferDays",
    friendlyName: "For how many days the coupon is considered new",
    type: "number",
    defaultValue: 0,
  },
  {
    name: "oneDayLabel",
    friendlyName: "Text to show when it's coupons last day",
    type: "string",
    defaultValue: "Last day!",
  },
  {
    name: "daysLabel",
    friendlyName: "Text to show of how many days the coupon is valid",
    type: "string",
    defaultValue: "{#days#} days left",
  },
  {
    name: "outOfStockLabel",
    friendlyName: "Label for out of stock items",
    type: "string",
    required: true,
    defaultValue: "Out of stock",
  },
  {
    name: "quantityAriaLabel",
    type: "string",
    friendlyName: "Quantity input aria label",
    defaultValue: "Quantity",
  },
  {
    name: "increaseAriaLabel",
    type: "string",
    friendlyName: "Increase quantity aria label",
    defaultValue: "Increase quantity",
  },
  {
    name: "decreaseAriaLabel",
    type: "string",
    friendlyName: "Decrease quantity aria label",
    defaultValue: "Decrease quantity",
  },
  {
    name: "categoryFilterTitle",
    type: "string",
    friendlyName: "Filters title",
    defaultValue: "Filters",
    required: true,
  },
  {
    name: "filtersTranslations",
    type: "list",
    friendlyName: "Filters",
    required: true,
    subFields: [
      {
        name: "filterKey",
        type: "string",
        friendlyName: "Filter key defined in Flotiq",
        required: true,
      },
      {
        name: "translatedFilterKey",
        type: "string",
        friendlyName: "Translated filter key",
        required: true,
      },
      {
        name: "image",
        type: "file",
        friendlyName: "Image",
        allowedFileTypes: IMAGE_FILE_TYPES,
        required: true,
      },
    ],
    defaultValue: [
      {
        image:
          "https://cdn.builder.io/api/v1/image/assets%2F90293f7dc9704cb5935f357315020051%2Fa9ff7d81326a40cba59334078ce44085",
        translatedFilterKey: "Restaurante",
        filterKey: "Restaurant",
      },
      {
        image:
          "https://cdn.builder.io/api/v1/image/assets%2F90293f7dc9704cb5935f357315020051%2Fef1ae9af4604416fb7ee36a299883fb5",
        translatedFilterKey: "Bars",
        filterKey: "Bar",
      },
      {
        image:
          "https://cdn.builder.io/api/v1/image/assets%2F90293f7dc9704cb5935f357315020051%2F8b96c3549e6c4cb587d485165e6da3bf",
        translatedFilterKey: "Cafes",
        filterKey: "Coffeshop",
      },
    ],
  },
] as const satisfies Inputs;

export const TrustedPlacesV2Component: RegisteredComponent = {
  name: "TrustedPlacesV2",
  component: dynamic(async () =>
    withValidation(trustedPlacesV2Inputs, (await import("./TrustedPlacesV2")).TrustedPlacesV2, "TrustedPlacesV2")
  ),
  inputs: trustedPlacesV2Inputs,
  requiresParent: {
    query: {
      "component.name": { $in: ["LoyaltyWrapper"] },
    },
    message: "'TrustedPlacesV2' component can only be added inside a 'LoyaltyWrapper'",
  },
};
